import React, { useEffect, useRef } from 'react';
import { Empty } from 'antd';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles({
    dropdown: {
        position: 'absolute',
        zIndex: 1000,
        background: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        maxHeight: '300px',
        width: '350px',
        overflow: 'auto',
    },
    sectionHeader: {
        padding: '8px 12px',
        fontSize: '11px',
        fontWeight: 600,
        color: '#8C8C8C',
        textTransform: 'uppercase',
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #f0f0f0',
    },
    listItem: {
        padding: '8px 12px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        '&:hover': {
            backgroundColor: '#e6f7ff',
        },
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    fieldName: {
        fontWeight: 500,
    },
    fieldType: {
        fontSize: '11px',
        color: '#8C8C8C',
        marginLeft: '8px',
    },
    badge: {
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '3px',
        backgroundColor: '#f0f0f0',
        color: '#595959',
    },
    emptyContainer: {
        padding: '20px',
    },
});

interface Field {
    prettyName: string;
    type: string;
    source: 'backend' | 'custom';
}

interface FieldReferenceAutocompleteProps {
    fields: Field[];
    onSelect: (fieldName: string) => void;
    onClose: () => void;
    position: { x: number; y: number };
    isManual?: boolean;
    searchQuery?: string;
}

export const FieldReferenceAutocomplete: React.FC<FieldReferenceAutocompleteProps> = ({
    fields,
    onSelect,
    onClose,
    position,
    isManual = false,
    searchQuery = '',
}: FieldReferenceAutocompleteProps) => {
    const classes = useStyles();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Close on ESC key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const displayFields = searchQuery
        ? fields.filter((f) => f.prettyName.toLowerCase().includes(searchQuery.toLowerCase()))
        : fields;
    const backendFields = displayFields.filter((f) => f.source === 'backend');
    const customFields = displayFields.filter((f) => f.source === 'custom');

    const getDropdownStyle = (): React.CSSProperties => {
        if (isManual) {
            return {
                top: position.y,
                left: position.x,
            };
        }
        return {
            top: position.y,
            left: Math.min(position.x, 50),
        };
    };

    return (
        <div ref={dropdownRef} className={classes.dropdown} style={getDropdownStyle()}>
            {displayFields.length === 0 ? (
                <div className={classes.emptyContainer}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('No fields found')}
                    />
                </div>
            ) : (
                <>
                    {backendFields.length > 0 && (
                        <>
                            <div className={classes.sectionHeader}>{t('Existing Metrics')}</div>
                            {backendFields.map((field) => (
                                <div
                                    key={`backend-${field.prettyName}`}
                                    className={classes.listItem}
                                    onClick={() => onSelect(field.prettyName)}
                                >
                                    <span>
                                        <span className={classes.fieldName}>{field.prettyName}</span>
                                        <span className={classes.fieldType}>({field.type})</span>
                                    </span>
                                </div>
                            ))}
                        </>
                    )}

                    {customFields.length > 0 && (
                        <>
                            <div className={classes.sectionHeader}>
                                {t('Custom Metrics (Added)')}
                            </div>
                            {customFields.map((field) => (
                                <div
                                    key={`custom-${field.prettyName}`}
                                    className={classes.listItem}
                                    onClick={() => onSelect(field.prettyName)}
                                >
                                    <span>
                                        <span className={classes.fieldName}>{field.prettyName}</span>
                                        <span className={classes.fieldType}>({field.type})</span>
                                    </span>
                                    <span className={classes.badge}>{t('Custom')}</span>
                                </div>
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default FieldReferenceAutocomplete;

